import React, { useEffect, useState } from 'react';
import { Calendar, Download, FileText } from 'lucide-react';
import { fetchReport, downloadReport, ReportParams, ScopeCity } from '../../lib/api';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { formatDanishNumber, formatDate } from '../../lib/utils';
import { ReportResponse } from '../../lib/types';
import { MONTH_NAMES } from './period';

type DateSelectionMode = 'month' | 'week' | 'day' | 'custom';

interface AalborgMonthlyReportProps {
    city: ScopeCity;
    year: number;
    month: number;
}

const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);
const weekOptions = Array.from({ length: 53 }, (_, i) => i + 1);

/**
 * Card for cities on the `aalborg-monthly` report engine
 * (GET /api/admin/reports/monthly): totals per parkeringsområde with commission
 * columns, downloadable as CSV or Excel.
 *
 * Rendered only for cities the signed-in account is scoped to — a Copenhagen
 * viewer never sees this card. That is presentation; the endpoint scopes its own
 * data to the caller's area grants regardless of what the page draws.
 */
const AalborgMonthlyReport: React.FC<AalborgMonthlyReportProps> = ({ city, year, month }) => {
    const [dateMode, setDateMode] = useState<DateSelectionMode>('month');
    const [week, setWeek] = useState(1);
    const [day, setDay] = useState(new Date().getDate());
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');

    const [reportData, setReportData] = useState<ReportResponse['data'] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Any change to the selected period invalidates the report on screen.
    useEffect(() => {
        setReportData(null);
    }, [dateMode, year, month, week, day, startTime, endTime]);

    const buildReportParams = (): ReportParams => {
        const params: ReportParams = { format: 'json' };

        switch (dateMode) {
            case 'month':
                params.year = year;
                params.month = month;
                break;
            case 'week':
                params.year = year;
                params.week = week;
                break;
            case 'day':
                params.year = year;
                params.month = month;
                params.day = day;
                break;
            case 'custom':
                if (startTime) params.startTime = new Date(startTime).toISOString();
                if (endTime) params.endTime = new Date(endTime).toISOString();
                break;
        }

        return params;
    };

    const handleGenerateReport = async () => {
        setIsLoading(true);
        setError(null);
        setReportData(null);

        try {
            const response = await fetchReport(buildReportParams()) as ReportResponse;

            if (response.success && response.data) {
                setReportData(response.data);
            } else {
                setError('Failed to generate report');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred while generating the report');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (format: 'csv' | 'excel') => {
        setIsDownloading(true);
        setError(null);

        try {
            await downloadReport(format, buildReportParams());
        } catch (err: any) {
            setError(err.message || `Failed to download ${format.toUpperCase()} report`);
        } finally {
            setIsDownloading(false);
        }
    };

    const modeButton = (mode: DateSelectionMode, label: string) => (
        <button
            key={mode}
            onClick={() => setDateMode(mode)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateMode === mode
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="space-y-6">
            <div className="bg-white border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    {city.settlementName} — månedsrapport
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                    Totaler pr. parkeringsområde med kommission. Standard er hele{' '}
                    {MONTH_NAMES[month - 1]} {year} — vælg en anden opdeling herunder hvis du skal bruge
                    en uge, en dag eller et frit interval.
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                    {modeButton('month', 'Month')}
                    {modeButton('week', 'Week')}
                    {modeButton('day', 'Day')}
                    {modeButton('custom', 'Custom Range')}
                </div>

                {dateMode !== 'month' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {dateMode === 'week' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Week (1-53)
                                </label>
                                <select
                                    value={week}
                                    onChange={(e) => setWeek(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    {weekOptions.map(w => (
                                        <option key={w} value={w}>Week {w}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {dateMode === 'day' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Day
                                </label>
                                <select
                                    value={day}
                                    onChange={(e) => setDay(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    {dayOptions.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {dateMode === 'custom' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        End Date
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-4">
                    <button
                        onClick={handleGenerateReport}
                        disabled={isLoading}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        <Calendar className="h-4 w-4" />
                        Generate report
                    </button>
                    {reportData && (
                        <>
                            <button
                                onClick={() => handleDownload('csv')}
                                disabled={isDownloading || reportData.quality?.readyForClose === false}
                                className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                <Download className="h-4 w-4" />
                                Download CSV
                            </button>
                            <button
                                onClick={() => handleDownload('excel')}
                                disabled={isDownloading || reportData.quality?.readyForClose === false}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                <FileText className="h-4 w-4" />
                                Download Excel
                            </button>
                        </>
                    )}
                </div>
            </div>

            {error && (
                <ErrorState
                    error={error}
                    onRetry={handleGenerateReport}
                />
            )}

            {isLoading && (
                <LoadingState message="Generating report..." />
            )}

            {reportData && !isLoading && (
                <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {city.settlementName} — report period
                        </h3>
                        <p className="text-sm text-gray-600">
                            {formatDate(reportData.period.start)} to {formatDate(reportData.period.end)}
                        </p>
                        {reportData.quality?.readyForClose === false && (
                            <div className="mt-3 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                                <div className="font-semibold">Report blocked — unresolved settlement data</div>
                                <div>
                                    {reportData.quality.exceptions.length} issue(s) across {reportData.quality.candidateSessions} candidate session(s).
                                    Downloads stay disabled until these are resolved.
                                </div>
                                <div className="mt-1 font-mono text-xs">
                                    {Array.from(new Set(reportData.quality.exceptions.map(issue => issue.code))).join(', ')}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parkeringsområde
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parkeringsområde nummer.
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Transaktioner
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parkeringstid i minutter
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Gennemsnitlige parkering i minutter
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parkeringsfee eks moms.
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parkringd fee moms
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Parkeringsfee inkl.moms
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        kommission eks. moms
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Kommission moms
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Kommission incl. VAT
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Beløb til at blive betalt
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {reportData.areas.map((area) => (
                                    <tr key={area.areaNo} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {area.areaName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {area.areaNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {area.transactions}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.totalParkingTimeMinutes, 2)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.avgParkingTimeMinutes, 2)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.parkingFeeExclVAT, 2)} DKK
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.parkingFeeVAT, 2)} DKK
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.parkingFeeInclVAT, 2)} DKK
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.commissionExclVAT, 2)} DKK
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.commissionVAT, 2)} DKK
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(area.commissionInclVAT, 2)} DKK
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {formatDanishNumber(area.totalAmount, 2)} DKK
                                        </td>
                                    </tr>
                                ))}
                                {/* Totals Row */}
                                <tr className="bg-gray-100 font-semibold">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        Totalsum
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        —
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {reportData.totals.transactions}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.totalParkingTimeMinutes, 2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.avgParkingTimeMinutes, 2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.parkingFeeExclVAT, 2)} DKK
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.parkingFeeVAT, 2)} DKK
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.parkingFeeInclVAT, 2)} DKK
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.commissionExclVAT, 2)} DKK
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.commissionVAT, 2)} DKK
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.commissionInclVAT, 2)} DKK
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDanishNumber(reportData.totals.totalAmount, 2)} DKK
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AalborgMonthlyReport;
